/**
 * Фильтр списка записей «шаг скрипта не выполнен» (#879, PR 5; спека
 * `docs/plans/2026-10-05-script-steps-report.md` §6).
 *
 * Отдельным файлом по той же причине, что `criteria-filter.ts`: схему читают и
 * список записей, и отчёт, а общий файл с `records.ts` замкнул бы цикл.
 *
 * Условие едет в query одной строкой `scriptStep=<stepId>:missed`, в форме
 * параметра `criterion`: ссылка из отчёта «Шаги скрипта» читается глазами.
 * Один шаг за раз — повтор параметра схема отвергает. Состояние одно,
 * `missed`: шаг должен был прозвучать и не выполнен — ровно `notCompleted`
 * отчёта (в зеркале D1 `completed = 0 AND applicable = 1`).
 */

import { z } from 'zod/v4';

/**
 * id шага — `script_steps.id`. Сегодня это UUID, но проверяется только
 * алфавит: без `:`, на котором держится разбор строки, и без всего, чего не
 * пустит литерал SQL (`record-metadata-queries.ts` проверяет его ещё раз сам).
 */
export const scriptStepIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'Invalid script step id');

export const RECORD_SCRIPT_STEP_STATES = ['missed'] as const;

export const recordScriptStepFilterSchema = z.object({
  stepId: scriptStepIdSchema,
  state: z.enum(RECORD_SCRIPT_STEP_STATES),
});

export type RecordScriptStepFilter = z.infer<typeof recordScriptStepFilterSchema>;

/** Условие → строка параметра `scriptStep`; обратное разбору в схеме запроса. */
export function formatRecordScriptStepFilter(filter: RecordScriptStepFilter): string {
  return `${filter.stepId}:${filter.state}`;
}

// id (64) + `:` + самое длинное состояние.
const FILTER_STRING_MAX =
  64 + 1 + Math.max(...RECORD_SCRIPT_STEP_STATES.map((state) => state.length));

/**
 * Параметр `scriptStep` списка записей. Разбор ничего не проверяет: строка не
 * той формы становится объектом, который отвергнет схема условия.
 */
export const recordScriptStepQuerySchema = z
  .string()
  .max(FILTER_STRING_MAX)
  .transform((raw) => {
    const [stepId = '', state, ...rest] = raw.split(':');
    return rest.length > 0 ? { stepId } : { stepId, state };
  })
  .pipe(recordScriptStepFilterSchema)
  .optional();

/**
 * Какой шаг применён фильтром — ответ списка называет его, чтобы чип фильтра
 * показал имя шага, а не id: сервер всё равно читает шаг, сверяя его с
 * организацией. Имена сегодняшние, как в отчёте.
 */
export const recordScriptStepFilterEchoSchema = z.object({
  stepId: z.string(),
  stepNumber: z.number().int(),
  stepName: z.string(),
  scriptName: z.string(),
});

export type RecordScriptStepFilterEcho = z.infer<typeof recordScriptStepFilterEchoSchema>;

/**
 * Код отказа «шаг не из скрипта организации» в поле `error` тела 400 — списка
 * записей и семантического поиска. Страница «Записи» отличает по нему этот
 * отказ от кривого параметра и снимает фильтр, поэтому код один на сервер и
 * интерфейс: разойдись они, фильтр перестал бы сниматься молча.
 */
export const UNKNOWN_SCRIPT_STEP_ERROR_CODE = 'unknown_script_step';
